// 영웅: 이동·4타 연계·회피·스킬·피격. 영웅 시간 영역(히트스톱·일시정지에만 멈춤)에서 매 틱 진행한다.
import { ATTACK_MAGNET, CANCEL, COMBO, DODGE, FIXED_DT, HERO, HURT, INPUT_BUFFER, KAEL_CANCEL_AT_BY_JOB, LINK, LYRA_CANCEL_AT_BY_JOB, SKILLS, SKILL_TUNING, REGION_STATUS, deg, heroCombo, heroMagnetRadius, heroSkillSpecs, type AttackSpec, type SkillSpec } from "../shared/config";
import { Ev } from "../shared/events";
import { BURST, BurstKind } from "../shared/growth";
import { KAEL_SKILLS } from "../shared/skills/kael";
import { heroSkills } from "../shared/skills/registry";
import type { BurstSlot, SkillHitbox, SkillTimeline } from "../shared/skills/types";
import { AttackPhase, BossState, Btn, HeroAct, MobState, SKILL_BTN_ALL, Scene, combatScene, type InputFrame, type PlayableHeroId } from "../shared/types";
import { HitSource } from "./combat";
import { effectiveMpCost } from "../shared/gear";
import type { GameSim } from "./game";

// 피격·선입력 값은 shared/config(COMBAT-FEEL 2장)에서 온다. 기존 이름은 별칭으로 유지한다.
export const HERO_HURT_TICKS = HURT.stun;
/** 피격 경직이 끝난 뒤 이어지는 무적(군중 속 경직 연타 방지) */
export const HERO_HURT_GRACE_TICKS = HURT.grace;
/** 선입력 버퍼: 누른 틱 포함 이후 이 틱 수까지 소비 가능 */
export const INPUT_BUFFER_TICKS = INPUT_BUFFER;
export const STORM_HIT_INTERVAL = SKILL_TUNING.stormHitInterval;
export const DASH_DISTANCE = SKILL_TUNING.dashDistance;
const MAGNET_COS = Math.cos((ATTACK_MAGNET.coneDeg * Math.PI) / 180);
/** 몸 이동이 돌진(dash)인 스킬 번호(돌진이 끊기면 지나간 길의 추가 판정을 예약한다) */
const DASH_ID = KAEL_SKILLS.find((t) => t.motion?.kind === 'dash')?.id ?? 0;
/** 폭풍의 눈(랭크 5) 추가 효과가 붙는 스킬 번호(끌어당김 반경 +3m 판정에 쓴다. 틱마다 배열을 훑지 않으려고 미리 찾아 둔다) */
const STORM_EYE_ID = KAEL_SKILLS.find((t) => t.hitboxes.some((hb) => hb.burst === 'stormEye'))?.id ?? 0;
/** 선입력 버퍼에 담는 버튼(이동·상호작용·일시정지는 제외) */
const BUFFER_BITS = [Btn.Attack, Btn.Dodge, Btn.Skill1, Btn.Skill2, Btn.Skill3, Btn.Skill4, Btn.Skill5, Btn.Awaken,
  Btn.Skill6, Btn.Skill7, Btn.Skill8, Btn.Skill9, Btn.Skill10, Btn.Skill11, Btn.Skill12, Btn.Skill13] as const;

/** 스킬 index(0..)를 actTick 틱 동안 썼을 때 다른 스킬로 캔슬할 수 있는지(LINK.cancelAt, 1 이상이면 불가). hero 생략 = 카엘 */
export function skillCancelOpen(index: number, actTick: number, hero: PlayableHeroId = 'kael', job: 0 | 1 = 0): boolean {
  const at = (hero === 'lyra' ? LYRA_CANCEL_AT_BY_JOB : KAEL_CANCEL_AT_BY_JOB)[job === 1 ? 1 : 0][index];
  const spec = heroSkillSpecs(hero, job)[index];
  return at !== undefined && spec !== undefined && at < 1 && actTick >= Math.ceil(spec.duration * at);
}


/** 도약 스킬(붉은 낙화) actTick t의 영웅 높이: 마지막 틱에 착지(0) */
function jumpHeight(tl: SkillTimeline, t: number): number {
  const mo = tl.motion;
  if (!mo || mo.kind !== 'jump') return 0;
  const landing = tl.duration - 1;
  return t < landing ? Math.sin((t / landing) * Math.PI) * mo.height : 0;
}
export class HeroController {
  private chainStep = -1;
  private chainAge = -1;
  private chainBuffered = false;
  private attackEmitted = false;
  /** 남은 피격 보호 틱(경직 + 경직 뒤 무적) */
  private graceTicks = 0;
  /** 버튼별 눌린 뒤 경과 틱. -1 = 버퍼 없음 */
  private readonly pressAge = new Int16Array(BUFFER_BITS.length).fill(-1);
  /** 돌진이 진행 중인지와 마지막으로 간 위치. 중간에 끊겨도 궤적 판정(DashWake)을 같은 경로로 예약하려고 기억한다 */
  private dashLive = false;
  private dashEndX = 0;
  private dashEndZ = 0;
  /** 이번 틱 이동 전 위치(경로 판정 path의 시작점) */
  private stepFromX = 0;
  private stepFromZ = 0;
  constructor(private readonly g: GameSim) {}

  /** 지금 영웅의 스킬 사양·타임라인·기본 공격 표(c022). 카엘은 SKILLS·KAEL_SKILLS·COMBO 그대로 */
  private get specs(): readonly SkillSpec[] { return heroSkillSpecs(this.g.heroId, this.g.jobRun.branch); }
  private get timelines(): readonly SkillTimeline[] { return heroSkills(this.g.heroId, this.g.jobRun.branch); }
  private get combo(): readonly AttackSpec[] { return heroCombo(this.g.heroId); }
  /** 지금 스킬이 도약(jump)이면 스킬이 y를 직접 정하므로 공통 낙하를 건너뛴다 */
  private jumping(): boolean {
    const h = this.g.hero;
    return h.act === HeroAct.Skill && this.timelines[h.skillId - 1]?.motion?.kind === 'jump';
  }

  reset(): void {
    const h = this.g.hero;
    h.y = h.prevY = 0; h.act = HeroAct.Idle; h.actTick = 0; h.comboStep = 0; h.skillId = 0;
    h.maxHp = this.g.stats.maxHp; h.maxMp = this.g.stats.maxMp; h.mpCostCut = this.g.stats.mpCostCut;
    const n = this.specs.length;
    for (let i = 0; i < h.cooldownMax.length; i++) h.cooldownMax[i] = i < n ? this.g.stats.skillCooldown[i] : 0;
    h.attackPhase = AttackPhase.Windup; h.hp = h.maxHp; h.mp = h.maxMp;
    h.cooldowns.fill(0); h.dodgeCooldown = 0; h.dodgeCancelCooldown = 0; h.awakenGauge = 0; h.invulnerable = false;
    this.clearChain(); this.attackEmitted = false; this.graceTicks = 0; this.pressAge.fill(-1);
    this.dashLive = false;
  }

  /** 부활 깃털(D2c): 쓰러지는 대신 그 자리에서 일어난다. 동작·연계를 끊고 ticks 동안 무적(피격 보호 구간과 같은 장치) */
  revive(ticks: number): void {
    const h = this.g.hero;
    this.clearChain(); this.clearLink(); this.pressAge.fill(-1); this.attackEmitted = false;
    h.act = HeroAct.Idle; h.actTick = 0; h.skillId = 0; h.y = h.prevY = 0;
    this.graceTicks = Math.max(this.graceTicks, ticks);
    h.invulnerable = true;
  }

  /** 창을 열 때 진행 중 이동 동작과 선입력을 함께 끊는다. HP·쿨다운은 건드리지 않는다. */
  stopForMenu(): void {
    this.clearChain(); this.clearLink(); this.pressAge.fill(-1); this.attackEmitted = false;
    const h = this.g.hero;
    h.act = HeroAct.Idle; h.actTick = 0; h.skillId = 0; h.y = h.prevY = 0;
    h.invulnerable = false;
    this.g.awakening.cancel();
  }

  private clearChain(): void { this.chainStep = -1; this.chainAge = -1; this.chainBuffered = false; }
  /** 연계 끊기(피격 경직·창 열기·시간 초과) */
  private clearLink(): void { const l = this.g.link; l.stage = 0; l.timer = 0; l.used = 0; l.cancelReady = 0; }

  /**
   * 피격 반영(피해는 combat이 이미 깎았다). 모든 피격 뒤 경직+무적 보호 구간을 둔다.
   * 일반 피격은 Idle/Run일 때만 경직을 주고, 공격·스킬·회피 중에는 피해만 받는다(행동 아머).
   * 보스 강타(heavy)는 항상 동작을 끊는다.
   */
  hurt(heavy: boolean): void {
    const h = this.g.hero;
    h.invulnerable = true;
    // 슈퍼아머 스킬(C9 혈검 회전베기)은 보스 강타도 행동 아머처럼 버틴다(피해는 이미 받았다)
    const armored = h.act === HeroAct.Skill && this.timelines[h.skillId - 1]?.props.superArmor === true;
    const interrupt = (heavy && !armored) || h.act === HeroAct.Idle || h.act === HeroAct.Run || h.act === HeroAct.Hurt;
    // 행동 아머로 버틴 피격은 짧게만 보호한다(군중 속 연타는 체력을 잃는다)
    if (!interrupt) { this.graceTicks = Math.max(this.graceTicks, HURT.armorGrace); return; }
    this.graceTicks = HERO_HURT_TICKS + HERO_HURT_GRACE_TICKS;
    this.clearChain(); this.clearLink();
    h.act = HeroAct.Hurt; h.actTick = 0; h.y = 0; h.skillId = 0;
  }

  /** 버퍼 나이 갱신 후, 아직 소비 가능한 버튼 비트를 돌려준다 */
  private updateBuffer(pressed: number): number {
    let mask = 0;
    for (let i = 0; i < BUFFER_BITS.length; i++) {
      const bit = BUFFER_BITS[i];
      if (pressed & bit) this.pressAge[i] = 0;
      else if (this.pressAge[i] >= 0 && ++this.pressAge[i] > INPUT_BUFFER_TICKS) this.pressAge[i] = -1;
      if (this.pressAge[i] >= 0) mask |= bit;
    }
    return mask;
  }

  private consume(bit: number): void {
    for (let i = 0; i < BUFFER_BITS.length; i++) if (BUFFER_BITS[i] === bit) this.pressAge[i] = -1;
  }

  /**
   * 공격 자석 대상 고르기(COMBAT-FEEL 2.3). 스틱을 잡았으면 스틱 방향 ±coneDeg 안, 아니면 반경 안에서 몸 가장자리가 가장 가까운 적.
   * g.magnet을 갱신하고 찾았으면 그 중심 방향(dirX, dirZ)을 magnetDir에 담는다. 객체를 만들지 않는다.
   */
  private pickMagnet(input: InputFrame): boolean {
    const g = this.g, h = g.hero, m = g.mobs, mg = g.magnet;
    const stick = input.moveX !== 0 || input.moveZ !== 0;
    const sl = stick ? Math.hypot(input.moveX, input.moveZ) : 1, sx = input.moveX / sl, sz = input.moveZ / sl;
    let best: number = heroMagnetRadius(g.heroId), id = -1, boss = false;
    for (let i = 0; i < m.capacity; i++) {
      // 등장 연출 중인 몹은 대상이 아니다(COMBAT-FEEL 2.3)
      // 기상 무적 중인 몹은 맞지 않으니 자석 대상에서 뺀다(다운은 추격 대상이라 남긴다)
      if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying || m.state[i] === MobState.Spawning || m.state[i] === MobState.Wakeup) continue;
      const dx = m.x[i] - h.x, dz = m.z[i] - h.z, d = Math.hypot(dx, dz), edge = d - g.mobController.radius(i);
      if (edge > best || (stick && d > 1e-6 && (dx * sx + dz * sz) / d < MAGNET_COS)) continue;
      best = edge; id = i;
    }
    const b = g.boss;
    if (this.bossTargetable()) {
      const dx = b.x - h.x, dz = b.z - h.z, d = Math.hypot(dx, dz), edge = d - b.radius;
      if (edge <= best && !(stick && d > 1e-6 && (dx * sx + dz * sz) / d < MAGNET_COS)) { best = edge; id = -1; boss = true; }
    }
    mg.target = id; mg.boss = boss;
    return id >= 0 || boss;
  }

  /** 보스를 자석 대상으로 삼을 수 있는 상태(등장 연출·쓰러짐 제외) */
  private bossTargetable(): boolean {
    const s = this.g.boss.state;
    return s !== BossState.Inactive && s !== BossState.Intro && s !== BossState.Dead && s !== BossState.Dying;
  }

  /** 자석 대상의 중심 좌표와 몸 반경. 대상이 없으면 false */
  private magnetPos(out: Float64Array): boolean {
    const g = this.g, mg = g.magnet;
    if (mg.boss) { out[0] = g.boss.x; out[1] = g.boss.z; out[2] = g.boss.radius; return true; }
    if (mg.target < 0) return false;
    const i = mg.target;
    out[0] = g.mobs.x[i]; out[1] = g.mobs.z[i]; out[2] = g.mobController.radius(i);
    return true;
  }
  private readonly magnetOut = new Float64Array(3);

  /** 공격·스킬 중 대상이 죽거나 사라졌으면 해제 */
  private validateMagnet(): void {
    const g = this.g, mg = g.magnet;
    if (mg.target >= 0) {
      const st = g.mobs.state[mg.target];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning || st === MobState.Wakeup) mg.target = -1;
    }
    if (mg.boss && !this.bossTargetable()) mg.boss = false;
  }

  private clearMagnet(): void { this.g.magnet.target = -1; this.g.magnet.boss = false; }

  /**
   * 방향 맞추기. auto(공격·스킬)면 공격 자석: 대상을 찾으면 그쪽으로 붙이고, 못 찾았으면 스틱 방향(없으면 유지).
   * auto가 아니면(회피) 스틱 방향만.
   */
  private aim(input: InputFrame, auto: boolean): void {
    const g = this.g, h = g.hero, stick = input.moveX !== 0 || input.moveZ !== 0;
    if (auto && combatScene(g.flow.scene) && this.pickMagnet(input) && this.magnetPos(this.magnetOut)) {
      const dx = this.magnetOut[0] - h.x, dz = this.magnetOut[1] - h.z;
      if (dx !== 0 || dz !== 0) { h.facing = Math.atan2(dx, dz); return; }
    }
    if (stick) h.facing = Math.atan2(input.moveX, input.moveZ);
  }

  private startAttack(step: number, input: InputFrame): void {
    const h = this.g.hero;
    this.consume(Btn.Attack);
    this.aim(input, true);
    h.act = HeroAct.Attack; h.actTick = 0; h.comboStep = step; h.attackPhase = AttackPhase.Windup; h.skillId = 0;
    this.chainBuffered = false; this.chainStep = -1; this.chainAge = -1; this.attackEmitted = false;
    this.g.combat.beginAction(HitSource.Basic);
  }

  private startDodge(input: InputFrame): void {
    const g = this.g, h = g.hero;
    this.consume(Btn.Dodge);
    this.aim(input, false); this.clearChain();
    h.act = HeroAct.Dodge; h.actTick = 0; h.skillId = 0; h.dodgeCooldown = DODGE.cooldown;
    g.beginDodgeWindow();
    const e = g.emit(Ev.Dodge); e.x = h.x; e.z = h.z; e.a = h.facing;
    // 전설 고유 효과 '회피의 방패': 회피하면 보호막
    g.combat.onDodge();
  }

  /** 스킬 index(0..4)를 쓸 수 있는지. 1~3은 처음부터 1랭크, 5(천공 올려베기)는 영웅 Lv5·4(진홍 초승달)는 Lv10부터 자동 습득이다(growthMods, docs/SKILL-TREE.md 11.1) */
  private unlocked(index: number): boolean {
    return this.g.gmods.skillRank[index] >= 1;
  }

  private startSkill(index: number, input: InputFrame): void {
    const g = this.g, h = g.hero, skill = this.specs[index];
    const cancelling = h.act === HeroAct.Skill;
    if (cancelling) { const c = g.emit(Ev.SkillCancel); c.x = h.x; c.z = h.z; c.a = h.skillId; c.b = skill.id; /* 공중 캔슬이면 높이를 유지하고 step()의 공통 낙하가 바닥까지 내린다(순간이동 금지) */ }
    this.consume(SKILL_BTN_ALL[index]);
    this.aim(input, true); this.clearChain();
    h.act = HeroAct.Skill; h.actTick = 0; h.skillId = skill.id;
    h.cooldowns[index] = g.stats.skillCooldown[index]; h.mp -= effectiveMpCost(skill.mpCost, h.mpCostCut);
    g.combat.onSkillCast(index);
    g.combat.beginAction(HitSource.Skill);
    const e = g.emit(Ev.SkillCast); e.x = h.x; e.z = h.z; e.a = skill.id; e.b = h.facing;
    this.linkSkill(index, cancelling);
  }

  /**
   * 연계 단계(설계도 rule-skill-link): 캔슬했거나 연계 시간 안이면 쓰지 않은 스킬로 단계+1, 아니면 이 스킬로 1단부터.
   * 3단 진입 때 각성 게이지를 한 번 더한다. 피해 배율은 combat이 g.link.stage를 읽는다.
   */
  private linkSkill(index: number, cancelling: boolean): void {
    const g = this.g, h = g.hero, l = g.link, bit = 1 << index;
    if (l.stage > 0 && (cancelling || l.timer > 0) && !(l.used & bit) && l.stage < LINK.maxStage) { l.stage++; l.used |= bit; }
    else { l.stage = 1; l.used = bit; }
    // 스킬 사용 중에는 시간이 줄지 않으므로 가득 채워 둔다(UI는 남은 시간으로 도장을 흐린다)
    l.timer = LINK.window; l.best = Math.max(l.best, l.stage);
    if (l.stage < 2) return;
    const up = g.emit(Ev.LinkUp); up.x = h.x; up.z = h.z; up.a = l.stage; up.b = this.specs[index].id; up.c = LINK.damage[l.stage - 1];
    if (l.stage < LINK.maxStage) return;
    l.finishes++;
    const finishGain = LINK.finishGauge * g.awakenMult();
    const gain = Math.min(finishGain, Math.max(0, 1 - h.awakenGauge));
    h.awakenGauge = Math.min(1, h.awakenGauge + finishGain);
    const f = g.emit(Ev.LinkFinish); f.x = h.x; f.z = h.z; f.a = l.finishes; f.b = gain;
    // 진홍 개화(캡스톤): 연계 3단 피니시 순간 주변에 폭발. LinkFinish 뒤에 낸다
    if (g.gmods.bursts & (1 << BurstKind.Bloom)) g.skillFx.bloom(h.x, h.z);
  }

  /** 기본 공격이 적을 맞히면 연계 시간을 다시 채운다(combat 호출). 스킬 사용 중에는 시간이 줄지 않으므로 무시 */
  refreshLink(): void {
    const l = this.g.link;
    if (l.stage > 0 && this.g.hero.act !== HeroAct.Skill) l.timer = LINK.window;
  }

  /** UI용: 지금 누르면 현재 스킬을 캔슬하고 나가는 스킬 비트 */
  private updateCancelReady(): void {
    const g = this.g, h = g.hero;
    let bits = 0;
    const specs = this.specs;
    if (combatScene(g.flow.scene) && h.act === HeroAct.Skill && skillCancelOpen(h.skillId - 1, h.actTick, g.heroId, g.jobRun.branch)) {
      for (let i = 0; i < specs.length; i++) if (i !== h.skillId - 1 && this.unlocked(i) && h.cooldowns[i] <= 0 && h.mp >= effectiveMpCost(specs[i].mpCost, h.mpCostCut)) bits |= 1 << i;
    }
    g.link.cancelReady = bits;
  }

  step(input: InputFrame): void {
    const g = this.g, h = g.hero;
    if (h.act === HeroAct.Dead) return;
    // 돌진이 끝까지 가지 못하고 끊겼다(캔슬·회피·피격): 간 만큼의 궤적을 지금 예약한다
    if (this.dashLive && !(h.act === HeroAct.Skill && h.skillId === DASH_ID && g.heroId === 'kael')) {
      this.dashLive = false;
      if (g.gmods.bursts & (1 << BurstKind.DashWake)) g.skillFx.scheduleDashWake(this.dashEndX, this.dashEndZ);
    }
    for (let i = 0; i < h.cooldowns.length; i++) if (h.cooldowns[i] > 0) h.cooldowns[i]--;
    if (h.dodgeCooldown > 0) h.dodgeCooldown--;
    if (h.dodgeCancelCooldown > 0) h.dodgeCancelCooldown--;
    g.tickJustDodgeCounter();
    if (this.graceTicks > 0) this.graceTicks--;
    // MP 회복 = 기본 × 레벨·트리 배율 × 장비 % + 유니크 고정 회복(BALANCE-1028 1절)
    h.mp = Math.min(h.maxMp, h.mp + (HERO.mpRegen * g.gmods.mpRegenMult * g.stats.mpRegenMult + g.stats.mpRegenFlat) * FIXED_DT);
    const want = this.updateBuffer(input.pressed);
    if (h.act === HeroAct.Awaken) {
      // 각성은 자석 대상을 쓰지도 유지하지도 않는다: 시작 직전 미리보기 대상 링이 각성 내내 남지 않게 지운다
      this.clearMagnet();
      g.awakening.step();
      if (this.graceTicks > 0) h.invulnerable = true;
      g.link.cancelReady = 0;
      return;
    }
    h.actTick++;
    // 연계 시간: 스킬 사용 중에는 멈추고, 그 밖에서 줄어 0이 되면 연계가 끝난다
    const l = g.link;
    if (l.stage > 0 && h.act !== HeroAct.Skill && l.timer > 0 && --l.timer === 0) this.clearLink();
    const combo = this.combo, specs = this.specs;
    if (h.act === HeroAct.Attack) {
      const spec = combo[h.comboStep], end = spec.windup + spec.active;
      h.attackPhase = h.actTick < spec.windup ? AttackPhase.Windup : h.actTick < end ? AttackPhase.Active : AttackPhase.Recovery;
      // 연계 창은 판정 종료 시점부터 센다. 선딜·판정 중 누른 공격은 버퍼에 남아 후딜 첫 틱에 연계로 잡힌다.
      if (h.actTick >= end && !spec.finisher) { this.chainStep = h.comboStep; this.chainAge = h.actTick - end; }
      if (!spec.finisher && h.attackPhase === AttackPhase.Recovery && this.chainAge <= spec.chainWindow && ((want | input.held) & Btn.Attack)) {
        this.chainBuffered = true;
        this.consume(Btn.Attack);
      }
      if (h.actTick >= end + spec.recovery) {
        if (this.chainBuffered && !spec.finisher) this.startAttack(h.comboStep + 1, input);
        else { h.act = HeroAct.Idle; h.actTick = 0; if (spec.finisher) this.clearChain(); }
      }
    } else if (h.act === HeroAct.Dodge && h.actTick >= DODGE.duration) { h.act = HeroAct.Idle; h.actTick = 0; }
    else if (h.act === HeroAct.Skill && h.actTick >= specs[h.skillId - 1].duration) { h.act = HeroAct.Idle; h.actTick = 0; h.y = 0; h.skillId = 0; l.timer = LINK.window; }
    else if (h.act === HeroAct.Hurt && h.actTick >= HERO_HURT_TICKS) { h.act = HeroAct.Idle; h.actTick = 0; }
    else if ((h.act === HeroAct.Idle || h.act === HeroAct.Run) && this.chainAge >= 0) this.chainAge++;

    // 이동 캔슬: 공격 후딜이 일정 비율 지나면 이동 입력으로 바로 달리기(연계 정보는 유지해 곧바로 다음 타로 이어갈 수 있다)
    if (h.act === HeroAct.Attack && h.attackPhase === AttackPhase.Recovery && !this.chainBuffered && (input.moveX !== 0 || input.moveZ !== 0)) {
      const spec = combo[h.comboStep], end = spec.windup + spec.active;
      // 같은 틱에 회피를 눌렀으면 이동 캔슬하지 않는다: 회피가 공격을 끊는 것으로 처리해야 회피 캔슬 쿨(3초)이 돈다
      if (h.actTick >= end + spec.recovery * CANCEL.moveAfterRecovery && !((want | input.held) & Btn.Attack) && !(want & Btn.Dodge)) {
        h.act = HeroAct.Run; h.actTick = 0;
        if (spec.finisher) this.clearChain();
      }
    }

    const ready = h.act === HeroAct.Idle || h.act === HeroAct.Run;
    const inAttack = h.act === HeroAct.Attack;
    // 공격 자석 미리보기(대상 링용): 대기·달리기 중 매 틱 계산하고, 공격·스킬 중에는 잡은 대상을 유지한다
    if (!combatScene(g.flow.scene)) this.clearMagnet();
    else if (ready) this.pickMagnet(input);
    else if (h.act === HeroAct.Attack || h.act === HeroAct.Skill) this.validateMagnet();
    else this.clearMagnet();
    // 스킬 캔슬(LINK): 스킬 길이의 cancelAt 비율이 지나면 다른 스킬로 바로 넘어간다(같은 스킬·낙화 제외)
    const inSkill = h.act === HeroAct.Skill;
    const skillCancel = inSkill && skillCancelOpen(h.skillId - 1, h.actTick, g.heroId, g.jobRun.branch);
    // 캔슬 규칙(COMBAT-FEEL 2.8): 각성은 공격 어느 단계든 끊고, 스킬은 판정 중이 아닐 때 끊는다(스킬 → 스킬은 cancelAt 이후).
    // 회피는 공격·스킬 어느 시점에서든 끊되, 끊을 때마다 회피 캔슬 쿨(DODGE.cancelCooldown)이 돈다. 대기·달리기 중 회피는 이 쿨과 무관하다.
    // 공격 시작은 대기·달리기에서만(연계는 위의 후딜 처리).
    const dodgeOk = ready || ((inAttack || inSkill) && h.dodgeCancelCooldown === 0);
    const skillOk = ready || (inAttack && h.attackPhase !== AttackPhase.Active) || skillCancel;
    if (combatScene(g.flow.scene) && (dodgeOk || skillOk)) {
      if ((want & Btn.Awaken) && (ready || inAttack) && g.awakening.start()) {
        this.consume(Btn.Awaken); this.clearChain(); g.awakening.step(true);
        return;
      }
      if (dodgeOk && (want & Btn.Dodge) && h.dodgeCooldown === 0) {
        if (!ready) h.dodgeCancelCooldown = DODGE.cancelCooldown;
        // 스킬을 회피로 끊어도 연계는 이어 붙일 수 있다(스킬이 끝난 것과 같다)
        // 공중(낙화)에서 회피로 끊어도 높이를 순간이동시키지 않는다: step()의 공통 낙하가 바닥까지 내린다(PR #152 Codex)
        if (inSkill) { h.skillId = 0; l.timer = LINK.window; }
        this.startDodge(input);
      } else if (skillOk) {
        let skillStarted = false;
        for (let i = 0; i < specs.length; i++) {
          if (!(want & SKILL_BTN_ALL[i]) || !this.unlocked(i) || h.cooldowns[i] > 0 || h.mp < effectiveMpCost(specs[i].mpCost, h.mpCostCut)) continue;
          if (inSkill && i === h.skillId - 1) continue;
          this.startSkill(i, input); skillStarted = true; break;
        }
        if (!skillStarted && ready && ((want | input.held) & Btn.Attack)) {
          const next = this.chainStep >= 0 && this.chainAge <= combo[this.chainStep].chainWindow ? this.chainStep + 1 : 0;
          this.startAttack(next, input);
        }
      }
    }
    let actInvulnerable = false;
    if (h.act === HeroAct.Attack) this.attack();
    else if (h.act === HeroAct.Dodge) {
      actInvulnerable = h.actTick < DODGE.iframes;
      g.moveHero((Math.sin(h.facing) * DODGE.dist) / DODGE.duration, (Math.cos(h.facing) * DODGE.dist) / DODGE.duration);
    } else if (h.act === HeroAct.Skill) this.skill();
    else if (h.act === HeroAct.Hurt) actInvulnerable = true;
    else if (h.act === HeroAct.Idle || h.act === HeroAct.Run) {
      const moving = input.moveX !== 0 || input.moveZ !== 0;
      const act = moving ? HeroAct.Run : HeroAct.Idle;
      if (h.act !== act) { h.act = act; h.actTick = 0; }
      if (moving) {
        h.facing = Math.atan2(input.moveX, input.moveZ);
        // 서리 둔화(D3a): 서리 정예 타격·서리 균열이 이동을 늦춘다(회피·스킬 돌진은 그대로)
        // 아바타 마을 이동 속도(레인 E, 편의): 마을 장면에서만 곱한다
        const run = HERO.runSpeed * g.rampageController.speedMult() * g.stats.moveSpeedMult * (h.chillTicks > 0 ? REGION_STATUS.chillSpeed : 1) * (g.flow.scene === Scene.Village ? g.townMoveMult : 1);
        g.moveHero(input.moveX * run * FIXED_DT, input.moveZ * run * FIXED_DT);
      }
    }
    // 공중 캔슬 낙하: 도약 스킬이 y를 정하는 동안이 아니면 남은 높이를 일정 속도로 바닥까지 내린다
    if (h.y > 0 && !this.jumping()) h.y = Math.max(0, h.y - HERO.airFallSpeed * FIXED_DT);
    h.invulnerable = actInvulnerable || this.graceTicks > 0;
    this.updateCancelReady();
  }

  private slash(range: number, arc: number, finisher: boolean, id: number): void {
    const g = this.g, h = g.hero, e = g.emit(Ev.Slash);
    e.x = h.x; e.z = h.z; e.a = h.facing; e.b = id; e.c = range; e.d = arc; e.flags = finisher ? 1 : 0;
  }

  private attack(): void {
    const g = this.g, h = g.hero, spec = this.combo[h.comboStep];
    if (h.attackPhase !== AttackPhase.Recovery) {
      let lunge = spec.lunge / (spec.windup + spec.active);
      // 돌진은 자석 대상 몸 앞에서 멈춘다(통과·겹침 금지, 이미 가까우면 제자리)
      if (lunge > 0 && this.magnetPos(this.magnetOut)) {
        const along = (this.magnetOut[0] - h.x) * Math.sin(h.facing) + (this.magnetOut[1] - h.z) * Math.cos(h.facing);
        if (along > 0) lunge = Math.min(lunge, Math.max(0, along - HERO.radius - this.magnetOut[2] - ATTACK_MAGNET.stopGap));
      }
      g.moveHero(Math.sin(h.facing) * lunge, Math.cos(h.facing) * lunge);
    }
    if (h.attackPhase !== AttackPhase.Active) return;
    // 원거리 기본 공격(c022 라이라): 판정 첫 틱에 화살 한 발(근접 부채꼴·참격 표시 없음). 화살이 날아가며 맞힌다
    if (spec.arrow) {
      if (!this.attackEmitted) {
        this.attackEmitted = true;
        g.arrows.fire(spec.arrow, spec.mult * g.gmods.basicMult, 0, spec.finisher, null);
      }
      return;
    }
    if (!this.attackEmitted) {
      this.slash(spec.range, spec.arc, spec.finisher, h.comboStep);
      this.attackEmitted = true;
      // 뇌명 검기(캡스톤): 마무리타 판정 순간 전방으로 번개 검기를 날린다
      if (spec.finisher && (g.gmods.bursts & (1 << BurstKind.Thunder))) g.skillFx.thunder(h.x, h.z, h.facing);
    }
    // 연격 숙련(트리): 기본 공격 피해 배율
    g.combat.cone(spec.range, spec.arc, spec.mult * g.gmods.basicMult, spec.finisher);
  }

  /**
   * 스킬 한 틱(스킬 타임라인 데이터를 읽어 실행한다, COMBAT-FEEL 2.10): 큐 → 몸 이동 → 몹 당김 → 히트박스.
   * 스킬 시작 틱이 actTick 0이고 길이만큼(duration 틱) 실행된다. 객체를 만들지 않는다.
   */
  private skill(): void {
    const g = this.g, h = g.hero, tl = this.timelines[h.skillId - 1], t = h.actTick, gm = g.gmods;
    // 스킬 피해 배율: 히트박스 배율 × 트리 랭크 배율(growthMods.skillMult)
    const rankMult = gm.skillMult[tl.id - 1];
    // 세계 위치 큐(at 'world')는 장판·폭발이 그 지점에서 낸다(game/arrows.ts)
    for (let k = 0; k < tl.cues.length; k++) if (tl.cues[k].tick === t && tl.cues[k].at !== 'world') this.cue(tl, k);
    this.stepFromX = h.x; this.stepFromZ = h.z;
    const mo = tl.motion;
    if (mo) {
      if (mo.kind === 'dash') {
        // 피의 돌진: 앞으로 돌진. 시작 틱에 돌진 시작점을 기억한다(핏빛 궤적·균열의 시작)
        // 핏빛 궤적(DashWake)은 카엘 돌진 전용이다(라이라 백스텝은 dash 이동만 쓴다)
        // C9: 혈검 회전베기(8)도 dash 이동을 쓰지만 핏빛 궤적은 피의 돌진만 남긴다
        if (t === 0 && g.heroId === 'kael' && tl.id === DASH_ID) { g.skillFx.dashStart(h.x, h.z); this.dashLive = true; }
        g.moveHero((Math.sin(h.facing) * mo.distance) / tl.duration, (Math.cos(h.facing) * mo.distance) / tl.duration);
      } else {
        // 붉은 낙화: 도약 후 마지막 틱에 착지
        h.y = jumpHeight(tl, t);
      }
    }
    const pl = tl.pull;
    if (pl && t >= pl.fromTick && t < pl.toTick) {
      // 칼날 폭풍 흡입(랭크 5 폭풍의 눈은 끌어당김 반경 +3m). 낙화는 착지 직전 한 점으로 모은다
      const eye = tl.id === STORM_EYE_ID && (gm.bursts & (1 << BurstKind.StormEye)) !== 0;
      this.pull(pl.radius + (eye ? BURST.stormEye.pullAdd : 0), pl.speed);
    }
    for (let k = 0; k < tl.hitboxes.length; k++) this.hitbox(tl, tl.hitboxes[k], t, rankMult);
  }

  /** 타임라인 큐 한 개를 이벤트로 낸다(렌더·오디오가 이름으로 고른다) */
  private cue(tl: SkillTimeline, k: number): void {
    const g = this.g, h = g.hero, c = tl.cues[k], e = g.emit(Ev.SkillCue);
    // y = 이 틱(몸 이동 뒤)의 영웅 높이: 렌더가 늦게(한 프레임에 여러 틱) 처리해도 최신 높이가 아니라 그 순간 높이에 띄운다
    // (도약·착지 큐의 바닥 연출이 공중에 뜨지 않게, Codex P2 c013). 큐는 몸 이동 전에 나므로 도약 높이는 같은 식으로 미리 구한다
    const y = tl.motion?.kind === 'jump' ? jumpHeight(tl, h.actTick) : h.y;
    e.x = h.x; e.y = y; e.z = h.z; e.a = tl.id; e.b = c.channel === 'vfx' ? 0 : c.channel === 'sfx' ? 1 : 2; e.c = k; e.d = h.facing;
    // C10-5: flags & 2 = 전직 갈래 1(카엘 소드 마스터·라이라 트랩 레인저, C10-6) — 렌더·오디오가 전직 스킬 큐를 그 갈래 표에서 찾는다
    e.flags = g.jobRun.branch === 1 ? 2 : 0;
  }

  /** 참격 표시 이벤트(Ev.Slash)를 낸다 */
  private slashFx(fx: { readonly range: number; readonly arcDeg: number; readonly finisher: boolean }, id: number): void {
    this.slash(fx.range, fx.arcDeg >= 360 ? Math.PI * 2 : deg(fx.arcDeg), fx.finisher, 10 + id);
  }

  /** 히트박스 하나의 이번 틱 실행. 시작·끝 틱 사이에서 everyTicks(없으면 1) 간격으로 판정한다 */
  private hitbox(tl: SkillTimeline, hb: SkillHitbox, t: number, rankMult: number): void {
    if (t < hb.startTick || t > hb.endTick) return;
    const every = hb.everyTicks ?? 1;
    if ((t - hb.startTick) % every !== 0) return;
    const g = this.g, h = g.hero, gm = g.gmods, sh = hb.shape, mult = hb.mult * rankMult;
    // 마지막 실행인지: 다음 반복이 끝 틱을 넘는다
    const last = t + every > hb.endTick;
    const repeating = hb.everyTicks !== undefined && hb.everyTicks > 1;
    const first = t === hb.startTick;
    const fx = hb.slash;
    // 새 판정 묶음(C9 공중 난무 착지): 앞 다단이 맞힌 대상도 다시 맞는다
    if (first && hb.freshPulse) { g.combat.newPulse(); g.combat.setMultiHit(false); }
    if (sh.kind === 'path') {
      if (fx && (!fx.firstOnly || first)) this.slashFx(fx, tl.id);
      g.combat.line(this.stepFromX, this.stepFromZ, h.x, h.z, sh.width, mult);
      this.dashEndX = h.x; this.dashEndZ = h.z;
      if (last) this.dashLive = false;
    } else if (sh.kind === 'circle') {
      // 다단 판정: 매 반복마다 같은 대상을 다시 맞힐 수 있고, 마지막 타격만 다단 중간타가 아니다
      if (repeating) { g.combat.newPulse(); g.combat.setMultiHit(!last); }
      if (fx) this.slashFx(fx, tl.id);
      const launch = hb.grants?.launch ?? null;
      g.combat.setLaunch(launch);
      g.combat.circle(sh.radius, mult, hb.grants?.finisher === true);
      g.combat.setLaunch(null);
    } else if (sh.kind === 'cone') {
      if (repeating) { g.combat.newPulse(); g.combat.setMultiHit(!last); }
      if (fx) this.slashFx(fx, tl.id);
      g.combat.setLaunch(hb.grants?.launch ?? null);
      g.combat.cone(sh.range, deg(sh.arcDeg), mult, hb.grants?.finisher === true);
      g.combat.setLaunch(null);
    } else if (sh.kind === 'line') {
      // 전방 띠(C9 대지 가르기 균열): 그 틱의 영웅 위치에서 facing으로 from~to 구간
      if (fx) this.slashFx(fx, tl.id);
      const dx = Math.sin(h.facing), dz = Math.cos(h.facing);
      g.combat.line(h.x + dx * sh.from, h.z + dz * sh.from, h.x + dx * sh.to, h.z + dz * sh.to, sh.width, mult, hb.grants?.finisher === true);
    } else if (sh.kind === 'arrow') {
      // 화살(c022 라이라): 발사 순간 facing으로 세계 투사체. 런처·마무리 부여는 화살이 맞힌 적에게 건다
      g.arrows.fire(sh.arrow, mult, tl.id, hb.grants?.finisher === true, hb.grants?.launch ?? null);
    } else if (sh.kind === 'zone') {
      // 장판(c022 라이라): 화살비·덫·회오리를 바닥 지점에 깐다
      g.arrows.place(sh.zone, mult, tl.id);
    } else {
      // 투사체: 발사 순간의 길로 검기가 나간다. 이후 영웅 동작과 무관하게 날아간다(세계 투사체)
      const dx = Math.sin(h.facing), dz = Math.cos(h.facing);
      g.skillFx.launchWave(h.x, h.z, dx, dz, mult);
      const e = g.emit(Ev.Slash);
      e.x = h.x; e.z = h.z; e.a = h.facing; e.b = 10 + tl.id; e.c = sh.length; e.d = sh.width;
    }
    if (last && hb.burst) this.burst(hb.burst, Math.sin(h.facing), Math.cos(h.facing), gm.bursts);
  }

  /** 트리 추가 효과(랭크 5·캡스톤)를 그 슬롯에서 실행한다. 배우지 않았으면 아무 일도 없다 */
  private burst(slot: BurstSlot, dx: number, dz: number, bits: number): void {
    const g = this.g, h = g.hero;
    switch (slot) {
      case 'dashWake': if (bits & (1 << BurstKind.DashWake)) g.skillFx.scheduleDashWake(h.x, h.z); break;
      case 'stormEye': if (bits & (1 << BurstKind.StormEye)) g.skillFx.stormEye(h.x, h.z); break;
      case 'aftershock': if (bits & (1 << BurstKind.Aftershock)) g.skillFx.scheduleAftershock(h.x, h.z); break;
      case 'twinMoon': if (bits & (1 << BurstKind.TwinMoon)) g.skillFx.scheduleTwinMoon(h.x, h.z, dx, dz); break;
    }
  }

  /** 반경 안 몹(등장·사망 연출 제외)을 영웅 쪽으로 speed(m/s)만큼 당긴다. 몸이 닿으면 멈추고 벽 충돌은 moveMob이 막는다 */
  private pull(radius: number, speed: number): void {
    const g = this.g, m = g.mobs, h = g.hero, step = speed * FIXED_DT;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) continue;
      const r = g.mobController.radius(i);
      const dx = h.x - m.x[i], dz = h.z - m.z[i], d = Math.hypot(dx, dz);
      if (d > radius || d <= HERO.radius + r) continue;
      const move = Math.min(d - HERO.radius - r, step);
      g.moveMob(i, (dx / d) * move, (dz / d) * move, r);
    }
  }
}
